
//===== BASIC TYPES: Dates, Language, Country

// fuller implementation at https://spin.atomicobject.com/2017/06/19/strongly-typed-date-string-typescript/
export type DateString = string;

export interface Country {
  id: string; // "US"
  name: string; // "United States"
  nameLocal: string; // United States
}

export enum ScriptDirection {
  "LTR",
  "RTL"
}

export interface Language {
  id: string; // "eng"
  name: string; // "English"
  nameLocal: string; // "English"
  script: string; // "Latin"
  scriptDirection: ScriptDirection; // ScriptDirection.LTR
}

//===== CHAPTER

export interface Chapter {
  id: string;
  bibleId: string;
  bookId: string;
  number: string;
  position: number;
  reference?: string;
  copyright?: string;
  content?: any;
  fumsId?: string;
}

export function isChapter(c: any): c is Chapter {
  return !!(c && c.id && c.bookId && c.number);
}

export function isChapterWithText(c: any): c is Chapter {
  return isChapter(c) && c.content;
}

//===== BOOK

export type BookId = string;

export interface Book {
  id: string;// "EXO"
  bibleId: string;// "555fef9a6cb31151-01"
  abbreviation: string;// "Ex"
  name: string;// "Exodus"
  nameLong: string;// "Exodus"
  chapters?: Chapter[];
}

export function isBook(b: any): b is Book {
  return !!(b && b.id && b.bibleId && b.abbreviation);
}

export function isBookWithChapters(b: any): b is Book {
  return !!(
    isBook(b) &&
    b.chapters &&
    b.chapters.length &&
    isChapter(b.chapters[0]) &&
    isChapter(b.chapters[b.chapters.length - 1])
  );
}

export function isBookWithText(b: any): b is Book {
  return !!(
    isBook(b) &&
    b.chapters &&
    b.chapters.length &&
    isChapterWithText(b.chapters[0]) &&
    isChapterWithText(b.chapters[b.chapters.length - 1])
  );
}

//===== BIBLES

type BibleType = string;
export const TEXT: BibleType = "text";
export const AUDIO: BibleType = "audio";

export const BibleIdRe = /[\da-fA-F]{16}-\d\d/;

export interface AudioBible {
  id: string;  // "4f12f582478c11e7-01"
  name: string;  // "English - 1991 Contemporary English (NT)"
  nameLocal: string;  // "English - 1991 Contemporary English (NT)"
}

export function isBibleId(id: string): boolean {
  return !!id.match(BibleIdRe);
}

export interface Bible {
  id: string;
  name?: string;
  nameLocal?: string;
  abbreviation?: string;
  abbreviationLocal?: string;
  description?: string;
  descriptionLocal?: string;
  language?: Language;
  countries?: Country[]; // | Country ??

  dblId?: string | null;
  relatedDbl?: string | null;
  type?: BibleType;
  updatedAt?: DateString;
  copyright?: string;
  info?: string;
  audioBibles: AudioBible[];

  books?: Book[];
}

export function isBible(bible: any): bible is Bible {
  return !!(bible && bible.id && bible.name && bible.abbreviation)
}

export function isBibleWithMetadata(bible: any): bible is Bible {
  return !!(isBible(bible)
    && bible.books
    && bible.books.length
    && isBookWithChapters(bible.books[0])
    && isBookWithChapters(bible.books[bible.books.length - 1])
  )
}

export function isBibleWithText(bible: any): bible is Bible {
  return !!(isBible(bible)
    && bible.books
    && bible.books.length
    && isBookWithText(bible.books[0])
    && isBookWithText(bible.books[bible.books.length - 1])
  )
}

// export const exampleBible: Bible = {
//   id: "555fef9a6cb31151-01",
//   dblId: "555fef9a6cb31151",
//   relatedDbl: null,
//   name: "Contemporary English Version",
//   nameLocal: "Contemporary English Version",
//   abbreviation: "CEV",
//   abbreviationLocal: "CEV",
//   description: "Contemporary English Version Protestant Bible",
//   descriptionLocal: "Contemporary English Version Protestant Bible",
//   language: {
//     id: "eng",
//     name: "English",
//     nameLocal: "English",
//     script: "Latin",
//     scriptDirection: ScriptDirection.LTR
//   },
//   countries: [
//     {
//       id: "US",
//       name: "United States",
//       nameLocal: "United States"
//     }
//   ],
//   type: BibleType.text,
//   updatedAt: "2019-05-04T07:09:43.000Z",
//   copyright:
//     "Contemporary English Version, Second Edition (CEV®) © 2006 American Bible Society. All rights reserved.",
//   info:
//     "<h3>Contemporary English Version (US Version)</h3> <p>Uncompromising simplicity marked the American Bible Society's translation of the Contemporary English Version that was first published in 1995. The text is easily read by grade schoolers, second language readers, and those who prefer the more contemporized form. The CEV is not a paraphrase; it is an accurate and faithful translation of the original manuscripts.</p> <p>After 200 years of ongoing ministry, American Bible Society invites people to experience the life-changing message of the Bible. Offering an increasing range of innovative ministries to address core life questions and struggles, the Bible Society partners with Christian churches and national Bible societies to share God’s Word both in the United States and around the globe.</p> <p>For more information, visit www.americanbible.org.</p> <p>To purchase a copy of this translation, visit www.bibles.com.</p>",
//   audioBibles: [
//     {
//       id: "4f12f582478c11e7-01",
//       name: "English - 1991 Contemporary English (NT)",
//       nameLocal: "English - 1991 Contemporary English (NT)"
//     },
//     {
//       id: "92b81d52146b11e7-01",
//       name: "English - 1995 Contemporary English (OT)",
//       nameLocal: "English - 1995 Contemporary English (OT)"
//     }
//   ]
// };


//===== BIBLE SITE

export interface Site {
  domain: string; // "eng"
  title?: string; // "dummy site"
  subdomain?: string;
  scriptDirection?: ScriptDirection;
  description?: string; // "Fake Site Stub"
  primaryColor?: string; // "#EEEEEE"
  secondaryColor?: string; // "#001122"
  bibleFont?: string; // "Noto Serif"
  uiFont?: string; // "Noto Sans"
  googleAnalyticsId?: string;
  bibles?: Bible[];
}

export function isSite(site: any): site is Site {
  return site && site.domain !== undefined;
}
